import { useContext, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Loader2, MessageCircle } from "lucide-react";
import { EASE } from "./Shared";
import { LangContext, whatsappUrl } from "../i18n";
import { ACCOMMODATION } from "../data/destinations";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const DEST_OPTIONS = {
  en: ["Prayagraj — Magh Mela", "Haridwar", "Nashik–Trimbakeshwar Simhastha 2027", "Ujjain Simhastha 2028", "Char Dham Yatra", "Jyotirlinga Yatra", "Other / not sure"],
  hi: ["प्रयागराज — माघ मेला", "हरिद्वार", "नासिक–त्र्यंबकेश्वर सिंहस्थ 2027", "उज्जैन सिंहस्थ 2028", "चारधाम यात्रा", "ज्योतिर्लिंग यात्रा", "अन्य / निश्चित नहीं"],
};

export default function EnquiryForm({ defaultDestination = "", defaultCategory = "", compact = false }) {
  const { lang, copy } = useContext(LangContext);
  const t = copy.enquiry;
  const [form, setForm] = useState({
    name: "", phone: "", destination: defaultDestination, arrival: "", departure: "",
    adults: 2, children: 0, category: defaultCategory, tents: 1, meals: true, transport: false,
    contact_pref: "whatsapp", message: "",
  });
  const [state, setState] = useState({ status: "idle", ref: null });
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const buildMessage = () => {
    const L = {
      en: { intro: "Namaste, I would like to enquire about a pilgrimage / Kumbh stay.", name: "Name", phone: "Phone", dest: "Destination", dates: "Dates", to: "to", guests: "Guests", adults: "adults", children: "children", cat: "Category", tents: "Rooms/Tents", meals: "Satvik meals: yes", transport: "Pickup/transport: yes", note: "Note" },
      hi: { intro: "नमस्ते, मैं तीर्थ यात्रा / कुंभ ठहराव की पूछताछ करना चाहता/चाहती हूँ।", name: "नाम", phone: "फ़ोन", dest: "गंतव्य", dates: "तिथियाँ", to: "से", guests: "यात्री", adults: "वयस्क", children: "बच्चे", cat: "श्रेणी", tents: "कक्ष/तंबू", meals: "सात्विक भोजन: हाँ", transport: "पिकअप/परिवहन: हाँ", note: "टिप्पणी" },
    }[lang];
    return [
      L.intro,
      `${L.name}: ${form.name}`, `${L.phone}: ${form.phone}`, `${L.dest}: ${form.destination}`,
      `${L.dates}: ${form.arrival} ${L.to} ${form.departure}`,
      `${L.guests}: ${form.adults} ${L.adults}, ${form.children} ${L.children}`,
      `${L.cat}: ${form.category}`, `${L.tents}: ${form.tents}`,
      form.meals ? L.meals : null, form.transport ? L.transport : null,
      form.message ? `${L.note}: ${form.message}` : null,
    ].filter(Boolean).join("\n");
  };

  const submit = async (e) => {
    e.preventDefault();
    setState({ status: "sending", ref: null });
    try {
      const res = await fetch(`${BACKEND}/api/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, adults: Number(form.adults), children: Number(form.children), tents: Number(form.tents), lang }),
      });
      if (!res.ok) throw new Error("submit failed");
      const data = await res.json();
      setState({ status: "success", ref: data.id });
    } catch (err) {
      setState({ status: "error", ref: null });
    }
  };

  if (state.status === "success") {
    return (
      <motion.div className="confirm-panel" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }} data-testid="enquiry-success">
        <CheckCircle2 size={40} className="confirm-icon" />
        <h3>{t.successTitle}</h3>
        <p>{t.successBody}</p>
        <p className="confirm-ref">{t.refLabel}: <strong data-testid="enquiry-ref">{state.ref.slice(0, 8).toUpperCase()}</strong></p>
        <a className="button" href={whatsappUrl(buildMessage())} target="_blank" rel="noopener noreferrer" data-testid="enquiry-whatsapp-followup">
          <MessageCircle size={17} /> {t.whatsappCta}
        </a>
      </motion.div>
    );
  }

  const input = (key, label, type = "text", props = {}) => (
    <label className="field" key={key}>
      <span>{label}</span>
      <input type={type} value={form[key]} onChange={set(key)} data-testid={`enquiry-${key}-input`} {...props} />
    </label>
  );

  return (
    <form className={`enquiry-form ${compact ? "compact" : ""}`} onSubmit={submit} data-testid="enquiry-form">
      <div className="form-grid">
        <label className="field">
          <span>{t.destination}</span>
          <select value={form.destination} onChange={set("destination")} required data-testid="enquiry-destination-select">
            <option value="" disabled>{t.selectDefault}</option>
            {DEST_OPTIONS[lang].map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
        {input("arrival", t.arrival, "date", { required: true })}
        {input("departure", t.departure, "date", { required: true })}
        {input("adults", t.adults, "number", { min: 1, max: 60, required: true })}
        {input("children", t.children, "number", { min: 0, max: 40 })}
        <label className="field">
          <span>{t.category}</span>
          <select value={form.category} onChange={set("category")} data-testid="enquiry-category-select">
            <option value="">{t.anyCategory}</option>
            {ACCOMMODATION.map((a) => <option key={a.id} value={a[lang].name}>{a[lang].name}</option>)}
          </select>
        </label>
        {input("tents", t.tents, "number", { min: 1, max: 20, required: true })}
        <label className="field">
          <span>{t.contactPref}</span>
          <select value={form.contact_pref} onChange={set("contact_pref")} data-testid="enquiry-contact-select">
            <option value="whatsapp">{t.whatsappOpt}</option>
            <option value="call">{t.callOpt}</option>
          </select>
        </label>
      </div>
      <div className="form-checks">
        <label className="check"><input type="checkbox" checked={form.meals} onChange={set("meals")} data-testid="enquiry-meals-check" /> <span>{t.meals}</span></label>
        <label className="check"><input type="checkbox" checked={form.transport} onChange={set("transport")} data-testid="enquiry-transport-check" /> <span>{t.transport}</span></label>
      </div>
      <div className="form-grid two">
        {input("name", t.name, "text", { required: true, minLength: 2 })}
        {input("phone", t.phone, "tel", { required: true, minLength: 6 })}
      </div>
      <label className="field">
        <span>{t.message}</span>
        <textarea rows="3" value={form.message} onChange={set("message")} data-testid="enquiry-message-input" />
      </label>
      <AnimatePresence>
        {state.status === "error" && (
          <motion.p className="form-error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="enquiry-error">
            {t.error} <a href={whatsappUrl(buildMessage())} target="_blank" rel="noopener noreferrer">WhatsApp</a>
          </motion.p>
        )}
      </AnimatePresence>
      <button type="submit" className="button form-submit" disabled={state.status === "sending"} data-testid="enquiry-submit-button">
        {state.status === "sending" ? <Loader2 size={17} className="spin" /> : <MessageCircle size={17} />}
        {state.status === "sending" ? t.sending : t.submit}
      </button>
    </form>
  );
}
